const { previewEnvironment } = require('./useNerpy');
const { useEffect, useState, useRef } = require('react');
const { context } = require('app-context');
const { default: Button } = require('@mui/material/Button');
const { default: TextField } = require('@mui/material/TextField');
const { default: Checkbox } = require('@mui/material/Checkbox');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');
const { default: Styled } = require('styled-components');

const internals = {};

module.exports = function OutreachAdmin({ schoolId, currentUserId, onAvailability }) {

    const [settings, setSettings] = useState(null);
    const [delivery, setDelivery] = useState(null);
    const activeScope = useRef(true);
    const [status, setStatus] = useState('');
    const [saving, setSaving] = useState(false);
    const { Panel, Question } = internals;
    useEffect(() => {

        let active = true;
        activeScope.current = true;
        onAvailability(false);
        if (previewEnvironment() && schoolId === 240394) {
            context.api.nearpeer.nerpy.outreachStatus().then((value) => {

                if (active) {
                    setDelivery(value);
                }
            }).catch(() => {});
            context.api.nearpeer.nerpy.outreachSettings().then((value) => {

                if (active) {
                    setSettings(value);
                    onAvailability(value.announcementsEnabled);
                }
            }).catch(() => {});
        }

        return () => { active = false; activeScope.current = false; };
    }, [schoolId, currentUserId]);

    if (!settings) {
        return null;
    }

    const update = (key, value) => setSettings((current) => ({ ...current, [key]: value }));
    const updateQuestion = (index, key, value) => update('questions', settings.questions.map((q, i) => i === index ? { ...q, [key]: value } : q));
    const cancel = async (id) => {

        try {
            await context.api.nearpeer.nerpy.cancelOutreach(id);
            const value = await context.api.nearpeer.nerpy.outreachStatus();
            if (activeScope.current) {
                setDelivery(value);
            }
        }
        catch {
            if (activeScope.current) {
                setStatus('The message could not be cancelled. Refresh to check its status.');
            }
        }
    };
    const save = async () => {

        setSaving(true);
        setStatus('');
        try {
            const saved = await context.api.nearpeer.nerpy.saveOutreachSettings(settings);
            if (!activeScope.current) {
                return;
            }

            setSettings(saved);
            onAvailability(saved.announcementsEnabled);
            setStatus('companion settings saved.');
        }
        catch {
            if (!activeScope.current) {
                return;
            }

            setStatus('Settings were not saved. Check the interval, email addresses, and staff profile IDs. Every enabled question needs a follow-up recipient.');
        }
        finally {
            if (activeScope.current) {
                setSaving(false);
            }
        }
    };

    return <Panel><h2>companion messages</h2>
        <p>Optional school messages from your companion. Check-ins wait until the student has not messaged their companion for the configured number of days. An unanswered check-in is not repeated. Students can pause these messages.</p>
        <FormControlLabel control={<Checkbox checked={settings.checkInsEnabled} onChange={(event) => update('checkInsEnabled', event.target.checked)} />} label='Enable inactivity check-ins' />
        <FormControlLabel control={<Checkbox checked={settings.announcementsEnabled} onChange={(event) => update('announcementsEnabled', event.target.checked)} />} label='Allow Send via companion for school announcements' />
        <TextField label='Days without contact before a check-in' type='number' value={settings.inactivityDays} inputProps={{ min: 1, max: 90 }} onChange={(event) => update('inactivityDays', Number(event.target.value))} />
        {[{ id: 2, name: 'Students' }, { id: 12, name: 'Prospects' }].map((role) => <FormControlLabel key={role.id} control={<Checkbox checked={settings.roles.includes(role.id)} onChange={(event) => update('roles', event.target.checked ? [...settings.roles, role.id] : settings.roles.filter((id) => id !== role.id))} />} label={role.name} />)}
        <p>“Yes, please” shares the student’s identity and this question with the contacts below. Other chat messages are not included. The school is responsible for follow-up. This is not counseling or an emergency monitoring service.</p>
        {delivery && <p>{delivery.emailEnabled ? 'Email delivery is enabled.' : 'Email delivery is disabled for this preview. Requests for email contacts will remain queued.'} {delivery.emails.filter((row) => ['failed', 'sending', 'pending'].includes(row.emailStatus)).map((row) => `${row.count} ${row.emailStatus}`).join('; ')}</p>}
        {settings.questions.map((q, index) => <Question key={q.id}>
            <TextField fullWidth multiline label={`Check-in question ${index + 1}`} value={q.text} onChange={(event) => updateQuestion(index, 'text', event.target.value)} inputProps={{ maxLength: 500 }} />
            <TextField fullWidth label='Bell recipients: school staff profile IDs, separated by commas' defaultValue={q.staffUserIds.join(', ')} onBlur={(event) => updateQuestion(index, 'staffUserIds', event.target.value.split(',').map((v) => Number(v.trim())).filter((v) => Number.isInteger(v) && v > 0))} helperText='Recipients must have Manage Notifications permission in this school.' />
            <TextField fullWidth multiline label='Email recipients, one per line' defaultValue={q.emails.join('\n')} onBlur={(event) => updateQuestion(index, 'emails', event.target.value.split('\n').map((value) => value.trim()).filter(Boolean))} />
        </Question>)}
        {!!delivery?.campaigns?.length && <div><h3>Queued companion messages</h3>{delivery.campaigns.map((campaign) => <p key={campaign.id}>{campaign.text} · {new Date(campaign.scheduledAt).toLocaleString()} <Button onClick={() => cancel(campaign.id)}>Cancel message</Button></p>)}</div>}
        <Button variant='contained' disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save companion settings'}</Button>
        <p role='status'>{status}</p>
    </Panel>;
};

internals.Panel = Styled.section`display: flex; flex-direction: column; gap: 12px; padding: 16px; margin-bottom: 24px; border: 1px solid ${({ theme }) => theme.palette?.divider || '#ddd'}; border-radius: 8px;`;
internals.Question = Styled.div`display: flex; flex-direction: column; gap: 12px; padding: 12px 0;`;
